import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import {
  CAP_GROUPS as SHARED_CAP_GROUPS,
  ALL_SUB_ADMIN_CAPS as SHARED_ALL_CAPS,
  CAP_IMPLIES as SHARED_CAP_IMPLIES,
} from '../../utils/capabilityCatalog';
import { DOBDatePicker, MonthPicker } from '../ui/date-picker';
import { 
  Shield, UserCog, User as UserIcon, Trash2, Crown, Users, Key, Eye, Copy, 
  CheckCircle2, Edit, Search, Filter, X, Download, Lock, Zap, RotateCcw, 
  AlertTriangle, Clock, Ban, RefreshCw, Loader2, History
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import Pagination from '../common/Pagination';
import { useAuth } from '../../context/AuthContext';
import { CAPS, hasCapability } from '../../utils/capabilities';
import UserActivityDrawer from './UserActivityDrawer';
import ExportModal from '../ExportModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  
  // Role-based access control
  const isFullAdmin = currentUser?.role === 'admin';
  // Activity History drawer is available to full admins and sub-admins with change_user_plan
  const canSeeActivity = isFullAdmin || hasCapability(currentUser, CAPS.CHANGE_USER_PLAN);
  const canManageUsers = isFullAdmin || hasCapability(currentUser, CAPS.MANAGE_USERS);
  const canChangeUserPlan = isFullAdmin || hasCapability(currentUser, CAPS.CHANGE_USER_PLAN);
  const [activityDrawerUser, setActivityDrawerUser] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('active');
  
  // Active users states
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  
  // Deleted users states
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [deletedPage, setDeletedPage] = useState(1);
  const [deletedItemsPerPage, setDeletedItemsPerPage] = useState(20);
  const [deletedTotal, setDeletedTotal] = useState(0);
  
  // Bulk selection states for deleted users
  const [selectedDeletedUsers, setSelectedDeletedUsers] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Blacklist states
  const [blacklist, setBlacklist] = useState([]);
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [loadingBlacklist, setLoadingBlacklist] = useState(false);
  const [blacklistSearch, setBlacklistSearch] = useState('');
  const [blacklistFilter, setBlacklistFilter] = useState('all'); // all, permanent, expiring
  const [blacklistPage, setBlacklistPage] = useState(1);
  const [blacklistItemsPerPage, setBlacklistItemsPerPage] = useState(20);
  const [blacklistTotal, setBlacklistTotal] = useState(0);
  
  // Add to blacklist dialog states
  const [addBlacklistDialogOpen, setAddBlacklistDialogOpen] = useState(false);
  const [newBlacklistIndos, setNewBlacklistIndos] = useState('');
  const [newBlacklistDuration, setNewBlacklistDuration] = useState('30');
  const [newBlacklistReason, setNewBlacklistReason] = useState('');
  const [addingToBlacklist, setAddingToBlacklist] = useState(false);
  
  // Edit blacklist duration dialog states
  const [editBlacklistDialogOpen, setEditBlacklistDialogOpen] = useState(false);
  const [editingBlacklistEntry, setEditingBlacklistEntry] = useState(null);
  const [editBlacklistDuration, setEditBlacklistDuration] = useState('30');
  const [updatingBlacklistDuration, setUpdatingBlacklistDuration] = useState(false);
  
  // Approved Admin Emails states
  const [approvedEmails, setApprovedEmails] = useState([]);
  const [loadingApprovedEmails, setLoadingApprovedEmails] = useState(false);
  const [approvedEmailsSearch, setApprovedEmailsSearch] = useState('');
  const [approvedEmailsPage, setApprovedEmailsPage] = useState(1);
  const [approvedEmailsTotal, setApprovedEmailsTotal] = useState(0);
  const [addApprovedEmailDialogOpen, setAddApprovedEmailDialogOpen] = useState(false);
  const [newApprovedEmail, setNewApprovedEmail] = useState('');
  const [newApprovedEmailNotes, setNewApprovedEmailNotes] = useState('');
  const [addingApprovedEmail, setAddingApprovedEmail] = useState(false);
  
  // Blocked Domains states
  const [blockedDomains, setBlockedDomains] = useState([]);
  const [loadingBlockedDomains, setLoadingBlockedDomains] = useState(false);
  const [blockedDomainsSearch, setBlockedDomainsSearch] = useState('');
  const [blockedDomainsPage, setBlockedDomainsPage] = useState(1);
  const [blockedDomainsTotal, setBlockedDomainsTotal] = useState(0);
  const [addBlockedDomainsDialogOpen, setAddBlockedDomainsDialogOpen] = useState(false);
  const [newBlockedDomains, setNewBlockedDomains] = useState('');
  const [addingBlockedDomains, setAddingBlockedDomains] = useState(false);
  
  // 2FA Reset states
  const [reset2FADialogOpen, setReset2FADialogOpen] = useState(false);
  const [resetting2FA, setResetting2FA] = useState(false);
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    subscription_plan: '',
    month_joined: '',
    plan_expiry_days: '',
    grade: '',
    mmd_location: '',
    plan_id: ''
  });
  // Full pricing-plan catalogue for the "specific plan" filter dropdown —
  // loaded once on mount and independent of the per-grade `availablePlans`
  // used by the plan-assignment dialog.
  const [allPlansForFilter, setAllPlansForFilter] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog states
  const [selectedUser, setSelectedUser] = useState(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  
  // Bulk selection states
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [bulkUpgradeDialogOpen, setBulkUpgradeDialogOpen] = useState(false);
  const [bulkUpgrading, setBulkUpgrading] = useState(false);

  // Export states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportCount, setExportCount] = useState(0);
  const [bulkUpgradeProgress, setBulkUpgradeProgress] = useState({ current: 0, total: 0 });
  
  // Form states
  const [selectedExpiryDays, setSelectedExpiryDays] = useState(30);
  const [selectedSubscribedGrade, setSelectedSubscribedGrade] = useState('');
  const [moduleAccessOverride, setModuleAccessOverride] = useState(false);
  const [deleteConfirmIndos, setDeleteConfirmIndos] = useState('');
  const [blacklistDuration, setBlacklistDuration] = useState('30');
  const [newPassword, setNewPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [editFormData, setEditFormData] = useState({ name: '', indos_number: '', date_of_birth: '', mobile_number: '' });
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  
  // Plan selection states
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedPlanModuleAccess, setSelectedPlanModuleAccess] = useState(null);
  // Plan-change modal mode. For free users this is always 'to_paid'. For paid
  // users the admin picks between 'switch' (paid → different paid plan) and
  // 'to_free' (existing hard-toggle downgrade). Default 'switch' to encourage
  // continuity over destructive downgrades.
  const [planChangeMode, setPlanChangeMode] = useState('switch');
  // Expiry mode for paid → paid switch: 'keep' (default, non-destructive),
  // 'prorate' (carry unused source value into target), 'reset' (admin picks
  // duration). Auto-hidden and forced to a safe default when source is
  // already expired (backend rejects prorate in that case anyway).
  const [expiryMode, setExpiryMode] = useState('keep');
  const [loadingPlans, setLoadingPlans] = useState(false);
  
  // Loading states
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  const availableGrades = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch active users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: currentPage, per_page: itemsPerPage });
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      Object.entries(filters).forEach(([key, value]) => { if (value) params.append(key, value); });
      
      const response = await axios.get(`${API}/admin/users?${params}`);
      setUsers(response.data.items || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filters]);

  // Load the full plan catalogue once so the "Plan" filter dropdown
  // can show every plan by name (grouped by grade). We keep archived
  // plans in the list too — admins often audit legacy cohorts.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/plans`);
        if (cancelled) return;
        const plans = (res.data || [])
          .slice()
          .sort((a, b) => {
            const ga = a.grade || 'zzz';
            const gb = b.grade || 'zzz';
            if (ga !== gb) return ga.localeCompare(gb);
            return (a.name || '').localeCompare(b.name || '');
          });
        setAllPlansForFilter(plans);
      } catch (e) {
        // Silent — the filter just won't populate; other flows are unaffected.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch deleted users (admin only)
  const fetchDeletedUsers = useCallback(async () => {
    if (!isFullAdmin) return;
    try {
      setLoadingDeleted(true);
      const response = await axios.get(`${API}/admin/users/deleted?page=${deletedPage}&per_page=${deletedItemsPerPage}`);
      setDeletedUsers(response.data.items || []);
      setDeletedTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load deleted users:', error);
      toast.error('Failed to load deleted users');
    } finally {
      setLoadingDeleted(false);
    }
  }, [isFullAdmin, deletedPage, deletedItemsPerPage]);

  // Fetch blacklist
  const fetchBlacklist = useCallback(async () => {
    try {
      setLoadingBlacklist(true);
      const params = new URLSearchParams({
        page: blacklistPage,
        per_page: blacklistItemsPerPage
      });
      if (blacklistSearch) params.append('search', blacklistSearch);
      if (blacklistFilter && blacklistFilter !== 'all') params.append('filter_type', blacklistFilter);
      
      const response = await axios.get(`${API}/admin/blacklist?${params}`);
      setBlacklist(response.data.items || []);
      setBlacklistTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load blacklist:', error);
      toast.error('Failed to load blacklist');
    } finally {
      setLoadingBlacklist(false);
    }
  }, [blacklistPage, blacklistItemsPerPage, blacklistSearch, blacklistFilter]);

  // Fetch approved admin emails
  const fetchApprovedEmails = useCallback(async () => {
    try {
      setLoadingApprovedEmails(true);
      const params = new URLSearchParams({
        page: approvedEmailsPage,
        per_page: 20
      });
      if (approvedEmailsSearch) params.append('search', approvedEmailsSearch);
      
      const response = await axios.get(`${API}/admin/approved-emails?${params}`);
      setApprovedEmails(response.data.items || []);
      setApprovedEmailsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load approved emails:', error);
      toast.error('Failed to load approved emails');
    } finally {
      setLoadingApprovedEmails(false);
    }
  }, [approvedEmailsPage, approvedEmailsSearch]);

  // Add approved email
  const handleAddApprovedEmail = async () => {
    if (!newApprovedEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    
    const email = newApprovedEmail.toLowerCase().trim();
    if (!email.endsWith('@helmtorq.com') && !email.endsWith('@marq.helmtorq.com')) {
      toast.error('Only @helmtorq.com or @marq.helmtorq.com emails allowed');
      return;
    }
    
    try {
      setAddingApprovedEmail(true);
      await axios.post(`${API}/admin/approved-emails`, {
        email: email,
        notes: newApprovedEmailNotes.trim() || null
      });
      toast.success('Email added to approved list');
      setAddApprovedEmailDialogOpen(false);
      setNewApprovedEmail('');
      setNewApprovedEmailNotes('');
      fetchApprovedEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add email');
    } finally {
      setAddingApprovedEmail(false);
    }
  };

  // Remove approved email
  const handleRemoveApprovedEmail = async (emailId) => {
    if (!window.confirm('Are you sure you want to remove this email from the approved list?')) return;
    
    try {
      await axios.delete(`${API}/admin/approved-emails/${emailId}`);
      toast.success('Email removed from approved list');
      fetchApprovedEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove email');
    }
  };

  // Fetch blocked domains
  const fetchBlockedDomains = useCallback(async () => {
    try {
      setLoadingBlockedDomains(true);
      const params = new URLSearchParams({
        page: blockedDomainsPage,
        per_page: 50
      });
      if (blockedDomainsSearch) params.append('search', blockedDomainsSearch);
      
      const response = await axios.get(`${API}/admin/blocked-domains?${params}`);
      setBlockedDomains(response.data.items || []);
      setBlockedDomainsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load blocked domains:', error);
      toast.error('Failed to load blocked domains');
    } finally {
      setLoadingBlockedDomains(false);
    }
  }, [blockedDomainsPage, blockedDomainsSearch]);

  // Add blocked domains
  const handleAddBlockedDomains = async () => {
    if (!newBlockedDomains.trim()) {
      toast.error('Enter at least one domain');
      return;
    }
    
    try {
      setAddingBlockedDomains(true);
      const response = await axios.post(`${API}/admin/blocked-domains`, {
        domains: newBlockedDomains
      });
      toast.success(response.data.message || 'Domains added');
      setAddBlockedDomainsDialogOpen(false);
      setNewBlockedDomains('');
      fetchBlockedDomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domains');
    } finally {
      setAddingBlockedDomains(false);
    }
  };

  // Remove blocked domain
  const handleRemoveBlockedDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to remove this domain from the blocked list?')) return;
    
    try {
      await axios.delete(`${API}/admin/blocked-domains/${domainId}`);
      toast.success('Domain removed from blocked list');
      fetchBlockedDomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove domain');
    }
  };

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { if (activeTab === 'deleted' && isFullAdmin) fetchDeletedUsers(); }, [activeTab, isFullAdmin, fetchDeletedUsers]);
  useEffect(() => { if (activeTab === 'blacklist' && isFullAdmin) fetchBlacklist(); }, [activeTab, isFullAdmin, fetchBlacklist]);
  useEffect(() => { if (activeTab === 'approved-emails' && isFullAdmin) fetchApprovedEmails(); }, [activeTab, isFullAdmin, fetchApprovedEmails]);
  useEffect(() => { if (activeTab === 'blocked-domains' && isFullAdmin) fetchBlockedDomains(); }, [activeTab, isFullAdmin, fetchBlockedDomains]);

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ role: '', subscription_plan: '', month_joined: '', plan_expiry_days: '', grade: '', mmd_location: '', plan_id: '' });
    setCurrentPage(1);
  };

  // Export to CSV (admin only)
  const exportToCSV = () => {
    if (!isFullAdmin) { toast.error('Admin rights required'); return; }
    if (users.length === 0) { toast.error('No users to export'); return; }
    
    const headers = ['Name', 'Email', 'INDoS', 'Grade', 'MMD', 'Role', 'Plan', 'Expires', 'Mobile', 'DOB', 'Joined'];
    const rows = users.map(u => [
      u.name || '', u.email || '', u.indos_number || '', u.grade || '', u.mmd_location || '',
      u.role || '', u.subscription_plan || 'free', u.plan_expires_at ? new Date(u.plan_expires_at).toLocaleDateString() : 'N/A',
      u.mobile_number || '', u.date_of_birth || '', u.created_at ? new Date(u.created_at).toLocaleDateString() : ''
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success(`Exported ${users.length} users`);
  };

  // Get plan badge with expiry date
  const getPlanBadge = (plan, expires, role, showExpiry = false, isPreview = false) => {
    if (role === 'admin' || role === 'sub_admin') {
      return (
        <div>
          <Badge className="bg-purple-600"><Crown className="w-3 h-3 mr-1" />{role === 'admin' ? 'Admin' : 'Sub Admin'}</Badge>
        </div>
      );
    }
    if (plan === 'paid') {
      // For preview mode (new plan), just show "Paid" without expiry calculation
      if (isPreview) {
        return <Badge className="bg-green-600">Paid</Badge>;
      }
      const daysLeft = expires ? Math.ceil((new Date(expires) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

      // Expired paid plans are displayed as Free — the backend filter treats
      // them the same way, so badge and filter stay consistent.
      if (expires && daysLeft <= 0) {
        return <Badge variant="outline">Free</Badge>;
      }

      const expiryDate = expires ? new Date(expires).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
      return (
        <div>
          <Badge className={daysLeft <= 7 ? 'bg-amber-500' : 'bg-green-600'}>
            {daysLeft <= 7 ? `${daysLeft}d left` : 'Paid'}
          </Badge>
          {showExpiry && expires && (
            <div className="text-xs text-gray-500 mt-0.5">Exp: {expiryDate}</div>
          )}
        </div>
      );
    }
    return <Badge variant="outline">Free</Badge>;
  };

  // Get role badge
  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { className: 'bg-purple-600', icon: Shield, label: 'Admin' },
      sub_admin: { className: 'bg-blue-600', icon: UserCog, label: 'Sub Admin' },
      user: { className: 'bg-gray-500', icon: UserIcon, label: 'User' }
    };
    const config = roleConfig[role] || roleConfig.user;
    return <Badge className={config.className}><config.icon className="w-3 h-3 mr-1" />{config.label}</Badge>;
  };

  // Fetch available plans for a grade
  const fetchPlansForGrade = async (grade) => {
    try {
      setLoadingPlans(true);
      const response = await axios.get(`${API}/admin/plans`);
      const allPlans = response.data || [];
      // Filter plans: either matching the grade or plans for "all grades" (grade is null)
      const filteredPlans = allPlans.filter(plan => 
        plan.is_active !== false && 
        (plan.grade === grade || !plan.grade || plan.grade === 'all')
      );
      
      // Sort plans: grade-specific first, then "all grades"
      const sortedPlans = filteredPlans.sort((a, b) => {
        const aIsGradeSpecific = a.grade === grade;
        const bIsGradeSpecific = b.grade === grade;
        if (aIsGradeSpecific && !bIsGradeSpecific) return -1;
        if (!aIsGradeSpecific && bIsGradeSpecific) return 1;
        return a.name.localeCompare(b.name);
      });
      
      setAvailablePlans(sortedPlans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      setAvailablePlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  // Group plans by type for display
  const getGroupedPlans = () => {
    const gradeSpecific = availablePlans.filter(p => p.grade === selectedSubscribedGrade);
    const allGrades = availablePlans.filter(p => !p.grade || p.grade === 'all');
    return { gradeSpecific, allGrades };
  };

  // Handle plan selection change
  const handlePlanSelectionChange = (planId) => {
    setSelectedPlanId(planId);
    if (planId) {
      const plan = availablePlans.find(p => p.id === planId);
      if (plan?.module_access) {
        setSelectedPlanModuleAccess(plan.module_access);
      } else {
        // Default all access if plan has no module_access configured
        setSelectedPlanModuleAccess({
          practice_sessions: true,
          progress_tracker: true,
          notebook: true,
          exam_insights: true
        });
      }
    } else {
      setSelectedPlanModuleAccess(null);
    }
  };

  // Handle plan change
  const handlePlanChangeClick = (user) => {
    setSelectedUser(user);
    const grade = user.subscribed_grade || user.grade || availableGrades[0];
    setSelectedSubscribedGrade(grade);
    setModuleAccessOverride(user.module_access_override || false);
    setSelectedExpiryDays(30);
    // Pre-select current plan when paid so admin sees "current" reflected
    // and only needs to change what they want to change.
    setSelectedPlanId(user.plan_id || user.subscription_plan_id || '');
    setSelectedPlanModuleAccess(null);
    // Default paid users to 'switch' mode (non-destructive). Free users
    // implicitly go through the upgrade flow.
    const currentExpiryDt = user.plan_expires_at ? new Date(user.plan_expires_at) : null;
    const isPaidAndLive = user.subscription_plan === 'paid' && currentExpiryDt && currentExpiryDt > new Date();
    // Expired paid users are effectively on free — force upgrade mode so
    // admin doesn't see nonsensical prorate options.
    setPlanChangeMode(isPaidAndLive ? 'switch' : 'to_paid');
    setExpiryMode('keep');
    fetchPlansForGrade(grade);
    setPlanDialogOpen(true);
  };

  // Handle grade change in upgrade dialog - refetch plans
  const handleGradeChangeInUpgrade = (grade) => {
    setSelectedSubscribedGrade(grade);
    setSelectedPlanId('');
    setSelectedPlanModuleAccess(null);
    fetchPlansForGrade(grade);
  };

  const handlePlanUpdate = async () => {
    if (!selectedUser) return;
    const isPaid = selectedUser.subscription_plan === 'paid';
    const isPrivilegedRole = ['admin', 'sub_admin'].includes(selectedUser.role);
    // Resolve effective target plan from the mode selector.
    // free user           → always upgrade to paid (existing flow)
    // paid + switch       → NEW: stays paid, applies new plan_id / grade / expiry
    // paid + to_free      → existing downgrade to free
    let newPlan;
    if (!isPaid) {
      newPlan = 'paid';
    } else if (planChangeMode === 'to_free') {
      newPlan = 'free';
    } else {
      newPlan = 'paid';   // switch mode — paid → paid
    }

    // Plan-picker required whenever moving TO paid (upgrade OR switch), unless
    // the user is an admin/sub_admin (they get paid by role, no plan doc).
    if (newPlan === 'paid' && !isPrivilegedRole && !selectedPlanId) {
      toast.error('Please select a plan');
      return;
    }

    try {
      setUpdatingPlan(true);
      const data = { subscription_plan: newPlan };
      if (newPlan === 'paid' && !isPrivilegedRole) {
      // Expiry: on switch mode the admin picks between 3 options via
      // `expiryMode`. On upgrade-from-free we always send today+N days.
      if (isPaid && planChangeMode === 'switch') {
        if (expiryMode === 'keep') {
          data.plan_expires_at = selectedUser.plan_expires_at;
        } else if (expiryMode === 'prorate') {
          data.prorate = true;
          // server computes plan_expires_at; do not send one
        } else {
          // reset
          data.plan_expires_at = new Date(Date.now() + selectedExpiryDays * 24 * 60 * 60 * 1000).toISOString();
        }
      } else {
        data.plan_expires_at = new Date(Date.now() + selectedExpiryDays * 24 * 60 * 60 * 1000).toISOString();
      }
      data.subscribed_grade = selectedSubscribedGrade;
      data.module_access_override = moduleAccessOverride;
      if (selectedPlanId) {
        data.plan_id = selectedPlanId;
      }
      }
      await axios.put(`${API}/admin/users/${selectedUser.id}/subscription`, data);
      const successMsg = (isPaid && planChangeMode === 'switch')
        ? 'Plan switched successfully'
        : `Plan updated to ${newPlan}`;
      toast.success(successMsg);
      setPlanDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    } finally {
      setUpdatingPlan(false);
    }
  };

  // ============ BULK SELECTION & UPGRADE ============
  
  // Toggle single user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Toggle all users on current page
  const toggleSelectAll = () => {
    // Only select free users (non-admin, non-sub_admin, not already paid)
    const eligibleUsers = users.filter(u => 
      u.subscription_plan !== 'paid' && 
      !['admin', 'sub_admin'].includes(u.role)
    );
    
    const allSelected = eligibleUsers.every(u => selectedUserIds.has(u.id));
    
    if (allSelected) {
      // Deselect all
      setSelectedUserIds(new Set());
    } else {
      // Select all eligible users
      setSelectedUserIds(new Set(eligibleUsers.map(u => u.id)));
    }
  };

  // Check if all eligible users are selected
  const isAllSelected = () => {
    const eligibleUsers = users.filter(u => 
      u.subscription_plan !== 'paid' && 
      !['admin', 'sub_admin'].includes(u.role)
    );
    return eligibleUsers.length > 0 && eligibleUsers.every(u => selectedUserIds.has(u.id));
  };

  // Get selected users data
  const getSelectedUsers = () => {
    return users.filter(u => selectedUserIds.has(u.id));
  };

  // Open bulk upgrade dialog
  const handleBulkUpgradeClick = () => {
    if (selectedUserIds.size === 0) {
      toast.error('Please select users to upgrade');
      return;
    }
    
    // Reset form states
    setSelectedSubscribedGrade(availableGrades[0]);
    setSelectedPlanId('');
    setSelectedPlanModuleAccess(null);
    setSelectedExpiryDays(30);
    setModuleAccessOverride(false);
    fetchPlansForGrade(availableGrades[0]);
    setBulkUpgradeDialogOpen(true);
  };

  // Execute bulk upgrade
  const handleBulkUpgrade = async () => {
    if (!selectedPlanId) {
      toast.error('Please select a plan');
      return;
    }

    const selectedUsers = getSelectedUsers();
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }

    setBulkUpgrading(true);
    setBulkUpgradeProgress({ current: 0, total: selectedUsers.length });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < selectedUsers.length; i++) {
      const user = selectedUsers[i];
      try {
        const data = {
          subscription_plan: 'paid',
          plan_expires_at: new Date(Date.now() + selectedExpiryDays * 24 * 60 * 60 * 1000).toISOString(),
          subscribed_grade: selectedSubscribedGrade,
          module_access_override: moduleAccessOverride,
          plan_id: selectedPlanId  // Backend expects 'plan_id'
        };
        
        await axios.put(`${API}/admin/users/${user.id}/subscription`, data);
        successCount++;
      } catch (error) {
        console.error(`Failed to upgrade user ${user.indos_number}:`, error);
        failCount++;
      }
      
      setBulkUpgradeProgress({ current: i + 1, total: selectedUsers.length });
    }

    setBulkUpgrading(false);
    setBulkUpgradeDialogOpen(false);
    setSelectedUserIds(new Set());
    
    if (failCount === 0) {
      toast.success(`Successfully upgraded ${successCount} users`);
    } else {
      toast.warning(`Upgraded ${successCount} users, ${failCount} failed`);
    }
    
    fetchUsers();
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedUserIds(new Set());
  };

  // ============ END BULK SELECTION & UPGRADE ============

  // Handle delete (soft delete with blacklist)
  const handleDeleteClick = (user) => {
    if (!isFullAdmin) { toast.error('Admin rights required'); return; }
    setSelectedUser(user);
    setDeleteConfirmIndos('');
    setBlacklistDuration('30');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser || deleteConfirmIndos.toUpperCase() !== selectedUser.indos_number?.toUpperCase()) {
      toast.error('INDoS number does not match');
      return;
    }
    
    try {
      setDeletingUser(true);
      await axios.delete(`${API}/admin/users/${selectedUser.id}`, {
        data: { confirm_indos: deleteConfirmIndos, blacklist_duration: blacklistDuration }
      });
      toast.success(`User deleted and INDoS blacklisted for ${blacklistDuration === 'forever' ? 'forever' : blacklistDuration + ' days'}`);
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  // Handle 2FA Reset
  const handleReset2FAClick = (user) => {
    setSelectedUser(user);
    setReset2FADialogOpen(true);
  };

  const handleReset2FAConfirm = async () => {
    if (!selectedUser) return;
    
    try {
      setResetting2FA(true);
      await axios.post(`${API}/admin/users/${selectedUser.id}/reset-2fa`);
      toast.success(`2FA has been reset for ${selectedUser.name}. They will need to set it up again on next login.`);
      setReset2FADialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset 2FA');
    } finally {
      setResetting2FA(false);
    }
  };

  // Handle restore
  const handleRestore = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/restore`);
      toast.success('User restored successfully');
      fetchDeletedUsers();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user');
    }
  };

  // Handle permanent delete
  const handlePermanentDelete = async (userId) => {
    if (!window.confirm('Permanently delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}/permanent`);
      toast.success('User permanently deleted');
      fetchDeletedUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Bulk selection handlers
  const handleSelectDeletedUser = (userId, checked) => {
    if (checked) {
      setSelectedDeletedUsers(prev => [...prev, userId]);
    } else {
      setSelectedDeletedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAllDeletedUsers = (checked) => {
    if (checked) {
      setSelectedDeletedUsers(deletedUsers.map(user => user.id));
    } else {
      setSelectedDeletedUsers([]);
    }
  };

  const isAllDeletedUsersSelected = deletedUsers.length > 0 && selectedDeletedUsers.length === deletedUsers.length;
  const isSomeDeletedUsersSelected = selectedDeletedUsers.length > 0 && selectedDeletedUsers.length < deletedUsers.length;

  // Bulk restore
  const handleBulkRestore = async () => {
    if (selectedDeletedUsers.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await axios.post(`${API}/admin/users/bulk-restore`, {
        user_ids: selectedDeletedUsers
      });
      toast.success(response.data.message);
      setSelectedDeletedUsers([]);
      fetchDeletedUsers();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore users');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk permanent delete
  const handleBulkPermanentDelete = async () => {
    if (selectedDeletedUsers.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await axios.post(`${API}/admin/users/bulk-permanent-delete`, {
        user_ids: selectedDeletedUsers
      });
      toast.success(response.data.message);
      setSelectedDeletedUsers([]);
      setBulkDeleteDialogOpen(false);
      fetchDeletedUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Clear selection when changing pages or items per page
  useEffect(() => {
    setSelectedDeletedUsers([]);
  }, [deletedPage, deletedItemsPerPage]);

  // Handle remove from blacklist
  const handleRemoveFromBlacklist = async (blacklistId) => {
    try {
      await axios.delete(`${API}/admin/blacklist/${blacklistId}`);
      toast.success('Removed from blacklist');
      fetchBlacklist();
    } catch (error) {
      toast.error('Failed to remove from blacklist');
    }
  };

  // Handle add to blacklist manually
  const handleAddToBlacklist = async () => {
    if (!newBlacklistIndos.trim()) {
      toast.error('Please enter an INDoS number');
      return;
    }
    try {
      setAddingToBlacklist(true);
      const durationDays = newBlacklistDuration === 'forever' ? null : parseInt(newBlacklistDuration);
      await axios.post(`${API}/admin/blacklist`, {
        indos_number: newBlacklistIndos.trim(),
        duration_days: durationDays,
        reason: newBlacklistReason || 'Manually added by admin'
      });
      toast.success('INDoS added to blacklist');
      setAddBlacklistDialogOpen(false);
      setNewBlacklistIndos('');
      setNewBlacklistDuration('30');
      setNewBlacklistReason('');
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to blacklist');
    } finally {
      setAddingToBlacklist(false);
    }
  };

  // Handle edit blacklist duration
  const handleEditBlacklistDuration = async () => {
    if (!editingBlacklistEntry) return;
    try {
      setUpdatingBlacklistDuration(true);
      const durationDays = editBlacklistDuration === 'forever' ? null : parseInt(editBlacklistDuration);
      await axios.put(`${API}/admin/blacklist/${editingBlacklistEntry.id}/duration`, {
        duration_days: durationDays
      });
      toast.success('Blacklist duration updated');
      setEditBlacklistDialogOpen(false);
      setEditingBlacklistEntry(null);
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update duration');
    } finally {
      setUpdatingBlacklistDuration(false);
    }
  };

  // Open edit blacklist duration dialog
  const openEditBlacklistDialog = (entry) => {
    setEditingBlacklistEntry(entry);
    if (entry.is_permanent) {
      setEditBlacklistDuration('forever');
    } else {
      // Estimate duration from days remaining
      const days = entry.days_remaining || 30;
      if (days <= 30) setEditBlacklistDuration('30');
      else if (days <= 90) setEditBlacklistDuration('90');
      else if (days <= 180) setEditBlacklistDuration('180');
      else if (days <= 365) setEditBlacklistDuration('365');
      else setEditBlacklistDuration('forever');
    }
    setEditBlacklistDialogOpen(true);
  };

  // Handle view details
  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    setViewDetailsDialogOpen(true);
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${API}/admin/users/${user.id}/details`);
      setUserDetails(response.data);
    } catch (error) {
      toast.error('Failed to load user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handle edit user
  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name || '',
      indos_number: user.indos_number || '',
      date_of_birth: user.date_of_birth || '',
      mobile_number: user.mobile_number || ''
    });
    setEditUserDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      setSavingEdit(true);
      await axios.put(`${API}/admin/users/${selectedUser.id}/edit`, editFormData);
      toast.success('User updated successfully');
      setEditUserDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle reset password
  const [emailSentInfo, setEmailSentInfo] = useState(null);

  const handleResetPasswordClick = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordCopied(false);
    setEmailSentInfo(null);
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    try {
      setResettingPassword(true);
      const response = await axios.post(`${API}/admin/users/${selectedUser.id}/reset-password`);
      // Backend may omit `new_password` once
      // RETURN_RESET_PASSWORD_IN_RESPONSE flag is flipped to "false" (post
      // emailit verification). Handle both shapes.
      setNewPassword(response.data.new_password || '');
      setEmailSentInfo({
        sent: !!response.data.email_sent,
        to: response.data.user_email,
        error: response.data.email_error,
      });
      const passwordShown = !!response.data.new_password;
      if (response.data.email_sent) {
        toast.success(
          passwordShown
            ? `Password reset. Temporary password is shown below and was emailed to ${response.data.user_email}.`
            : `Password reset email sent to ${response.data.user_email}.`
        );
      } else {
        toast.warning(
          passwordShown
            ? 'Password reset but email delivery failed. Share the password manually using a secure channel.'
            : 'Password reset but email delivery failed and password is hidden in this environment. Re-enable RETURN_RESET_PASSWORD_IN_RESPONSE or fix email delivery.'
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(newPassword);
    setPasswordCopied(true);
    toast.success('Password copied');
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  // Handle role change (admin only)
  // Capability catalog is now sourced from a shared module so User
  // Management + Admin Docs Manager stay in sync when new caps ship.
  // See utils/capabilityCatalog.js.
  // `manage_classes` implies `host_classes` (handled at check-time
  // server-side); the modal below auto-checks + locks `host_classes`
  // when `manage_classes` is on.
  const CAP_GROUPS = SHARED_CAP_GROUPS;
  const ALL_SUB_ADMIN_CAPS = SHARED_ALL_CAPS;
  const CAP_IMPLIES = SHARED_CAP_IMPLIES;

  const handleRoleClick = (user) => {
    if (!isFullAdmin) { toast.error('Admin rights required'); return; }
    setSelectedUser(user);
    setSelectedRole(user.role);
    const existing = Array.isArray(user.permissions) ? user.permissions : [];
    if (user.role === 'sub_admin') {
      const filtered = existing.filter((p) => ALL_SUB_ADMIN_CAPS.includes(p));
      setSelectedPermissions(filtered);
    } else {
      // Default: empty when promoting to sub_admin — super-admin explicitly ticks caps.
      setSelectedPermissions([]);
    }
    setRoleDialogOpen(true);
  };

  const togglePermission = (cap) => {
    setSelectedPermissions((prev) => {
      const has = prev.includes(cap);
      if (has) {
        // Unchecking a superset also unchecks the implied caps' explicit rows
        // (they'll no longer be locked). Leave implied caps as-is otherwise.
        return prev.filter((c) => c !== cap);
      }
      // Checking a superset auto-adds the implied caps.
      const implied = CAP_IMPLIES[cap] || [];
      const next = new Set(prev);
      next.add(cap);
      for (const imp of implied) next.add(imp);
      return [...next];
    });
  };

  // True if `cap` is currently locked-on because a superset owning it is selected.
  const isImpliedBySelected = (cap) => {
    for (const [parent, children] of Object.entries(CAP_IMPLIES)) {
      if (children.includes(cap) && selectedPermissions.includes(parent)) return true;
    }
    return false;
  };

  const handleRoleUpdate = async () => {
    try {
      const body = selectedRole === 'sub_admin' ? { permissions: selectedPermissions } : {};
      await axios.put(`${API}/admin/users/${selectedUser.id}/role?role=${selectedRole}`, body);
      toast.success('Role updated successfully');
      setRoleDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  // Export handlers
  const fetchExportCount = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.role) params.append('role', filters.role);
      if (filters.subscription_plan) params.append('subscription_plan', filters.subscription_plan);
      if (filters.grade) params.append('grade', filters.grade);
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      
      const response = await axios.get(`${API}/export/users/count?${params}`);
      setExportCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch export count:', error);
    }
  };

  const handleExport = async (format, activeFilters, selectedColumns) => {
    try {
      setExporting(true);
      
      const params = new URLSearchParams();
      params.append('format', format);
      // Use current page filters
      if (filters.role) params.append('role', filters.role);
      if (filters.subscription_plan) params.append('subscription_plan', filters.subscription_plan);
      if (filters.grade) params.append('grade', filters.grade);
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (selectedColumns.length > 0) params.append('columns', selectedColumns.join(','));
      
      const response = await axios.get(`${API}/export/users?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completed successfully');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Fetch export count when export modal opens
  useEffect(() => {
    if (showExportModal) {
      fetchExportCount();
    }
  }, [showExportModal, filters, debouncedSearchTerm]);

  // Render user row
  const renderUserRow = (user, isDeleted = false) => {
    const canSelect = !isDeleted && user.subscription_plan !== 'paid' && !['admin', 'sub_admin'].includes(user.role);
    const isSelected = selectedUserIds.has(user.id);
    
    return (
      <TableRow key={user.id} className={isSelected ? 'bg-blue-50' : ''}>
        {!isDeleted && (
          <TableCell>
            {canSelect ? (
              <Checkbox 
                checked={isSelected}
                onCheckedChange={() => toggleUserSelection(user.id)}
                aria-label={`Select ${user.name}`}
              />
            ) : (
              <div className="w-4 h-4" /> 
            )}
          </TableCell>
        )}
        <TableCell>
          <div className="font-medium">{user.name}</div>
          <div className="text-xs text-gray-500">{user.email}</div>
        </TableCell>
        <TableCell>
          <div className="font-mono text-sm">{user.indos_number || '-'}</div>
        </TableCell>
        <TableCell>{getRoleBadge(user.role)}</TableCell>
        <TableCell>
          <div className="text-sm">{user.grade || '-'}</div>
          <div className="text-xs text-gray-500">{user.mmd_location || ''}</div>
        </TableCell>
        <TableCell>{getPlanBadge(user.subscription_plan, user.plan_expires_at, user.role, true)}</TableCell>
        <TableCell className="text-sm text-gray-500">
          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
        </TableCell>
        <TableCell>
          {isDeleted ? (
            <div className="flex items-center gap-2">
              <Badge variant="destructive"><Clock className="w-3 h-3 mr-1" />{user.days_remaining}d left</Badge>
              <Button size="sm" variant="outline" onClick={() => handleRestore(user.id)} title="Restore">
                <RotateCcw className="w-4 h-4" />
              </Button>
              {isFullAdmin && (
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handlePermanentDelete(user.id)} title="Delete Permanently">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 w-8 p-0">
                  <Zap className="w-4 h-4 text-amber-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => handleViewDetails(user)}>
                  <Eye className="w-4 h-4 mr-2" />View Details
                </DropdownMenuItem>
                {canSeeActivity && (
                  <DropdownMenuItem onClick={() => setActivityDrawerUser(user)} data-testid={`user-activity-history-${user.id}`}>
                    <History className="w-4 h-4 mr-2" />Activity History
                  </DropdownMenuItem>
                )}
                {/*
                  Sub-admins can only act on regular users (server-side tier
                  check enforces this; hiding here matches the "if you can't
                  do it, you don't see the button" convention).
                */}
                {canManageUsers && (isFullAdmin || user.role === 'user') && (
                  <DropdownMenuItem onClick={() => handleEditClick(user)}>
                    <Edit className="w-4 h-4 mr-2" />Edit Info
                  </DropdownMenuItem>
                )}
                {canManageUsers && (isFullAdmin || user.role === 'user') && (
                  <DropdownMenuItem onClick={() => handleResetPasswordClick(user)}>
                    <Key className="w-4 h-4 mr-2" />Reset Password
                  </DropdownMenuItem>
                )}
                {/* Reset 2FA — hidden for the env-whitelisted super-admin (their 2FA can only be reset by themselves). All other tier protections enforced server-side. */}
                {canManageUsers && (isFullAdmin || user.role === 'user') && user.totp_enabled && !user.is_super_admin && (
                  <DropdownMenuItem onClick={() => handleReset2FAClick(user)}>
                    <Shield className="w-4 h-4 mr-2" />Reset 2FA
                  </DropdownMenuItem>
                )}
                {canChangeUserPlan && (isFullAdmin || user.role === 'user') && <DropdownMenuSeparator />}
                {canChangeUserPlan && (isFullAdmin || user.role === 'user') && (
                  <DropdownMenuItem onClick={() => handlePlanChangeClick(user)}>
                    <Crown className="w-4 h-4 mr-2" />Change Plan
                  </DropdownMenuItem>
                )}
              {isFullAdmin && (
                <>
                  <DropdownMenuItem onClick={() => handleRoleClick(user)}>
                    <Shield className="w-4 h-4 mr-2" />Change Role
                  </DropdownMenuItem>
                  {/* Delete — hidden for the env-whitelisted super-admin. Backend also enforces this (defense-in-depth). */}
                  {!user.is_super_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleDeleteClick(user)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />Delete User
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-6 h-6 text-[#0A7EA4]" />
            User Management
          </CardTitle>
          <div className="flex items-center gap-2">
            {isFullAdmin && (
              <Button 
                variant="outline" 
                onClick={() => setShowExportModal(true)} 
                disabled={users.length === 0}
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                <Download className="w-4 h-4 mr-2" />Export
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="active">
              <Users className="w-4 h-4 mr-2" />Active Users
            </TabsTrigger>
            {isFullAdmin && (
              <TabsTrigger value="deleted">
                <Trash2 className="w-4 h-4 mr-2" />Deleted Users
              </TabsTrigger>
            )}
            {isFullAdmin && (
              <TabsTrigger value="blacklist">
                <Ban className="w-4 h-4 mr-2" />Blacklist
              </TabsTrigger>
            )}
            {isFullAdmin && (
              <TabsTrigger value="approved-emails">
                <Shield className="w-4 h-4 mr-2" />Approved Admin Emails
              </TabsTrigger>
            )}
            {isFullAdmin && (
              <TabsTrigger value="blocked-domains">
                <Ban className="w-4 h-4 mr-2" />Blocked Domains
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="active">
            {/* Search and Filters */}
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, email, or INDoS..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
                <Filter className="w-4 h-4 mr-2" />
                {showFilters ? 'Hide' : 'Filters'}
                {Object.values(filters).some(v => v) && (
                  <Badge variant="secondary" className="ml-2">{Object.values(filters).filter(v => v).length}</Badge>
                )}
              </Button>
              {(searchTerm || Object.values(filters).some(v => v)) && (
                <Button variant="ghost" onClick={clearFilters}><X className="w-4 h-4 mr-2" />Clear</Button>
              )}
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <Card className="p-4 mb-4 bg-gray-50">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div>
                    <Label className="text-xs">Role</Label>
                    <Select value={filters.role} onValueChange={(v) => { setFilters(f => ({ ...f, role: v })); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="sub_admin">Sub Admin</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Plan</Label>
                    <Select value={filters.subscription_plan} onValueChange={(v) => { setFilters(f => ({ ...f, subscription_plan: v })); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Plans</SelectItem>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Specific Plan</Label>
                    <Select
                      value={filters.plan_id || 'all'}
                      onValueChange={(v) => { setFilters(f => ({ ...f, plan_id: v === 'all' ? '' : v })); setCurrentPage(1); }}
                    >
                      <SelectTrigger data-testid="user-filter-plan-id">
                        <SelectValue placeholder="Any plan" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any plan</SelectItem>
                        {allPlansForFilter.map((p) => (
                          <SelectItem key={p.id} value={p.id} data-testid={`user-filter-plan-opt-${p.id}`}>
                            {p.name}
                            {p.grade ? ` · ${p.grade}` : ''}
                            {p.is_archived ? ' (archived)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Expiring In</Label>
                    <Select value={filters.plan_expiry_days || 'any'} onValueChange={(v) => { setFilters(f => ({ ...f, plan_expiry_days: v === 'any' ? '' : v })); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Time</SelectItem>
                        <SelectItem value="7">Next 7 days</SelectItem>
                        <SelectItem value="30">Next 30 days</SelectItem>
                        <SelectItem value="60">Next 60 days</SelectItem>
                        <SelectItem value="90">Next 90 days</SelectItem>
                        <SelectItem value="120">Next 120 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Grade</Label>
                    <Select value={filters.grade} onValueChange={(v) => { setFilters(f => ({ ...f, grade: v })); setCurrentPage(1); }}>
                      <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Grades</SelectItem>
                        {availableGrades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Joined Month</Label>
                    <MonthPicker 
                      value={filters.month_joined} 
                      onChange={(value) => { setFilters(f => ({ ...f, month_joined: value })); setCurrentPage(1); }}
                      placeholder="Select month"
                      fromYear={2020}
                      toYear={new Date().getFullYear() + 1}
                    />
                  </div>
                </div>
              </Card>
            )}

            {/* Bulk Actions Bar */}
            {selectedUserIds.size > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    {selectedUserIds.size} user{selectedUserIds.size > 1 ? 's' : ''} selected
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearSelection}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    Clear selection
                  </Button>
                </div>
                {canChangeUserPlan && (
                  <Button 
                    onClick={handleBulkUpgradeClick}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="bulk-upgrade-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Bulk Upgrade to Paid
                  </Button>
                )}
              </div>
            )}

            {/* Users Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox 
                        checked={isAllSelected()}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>INDoS</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Grade/MMD</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="w-[60px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8">Loading...</TableCell></TableRow>
                  ) : users.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No users found</TableCell></TableRow>
                  ) : (
                    users.map(user => renderUserRow(user))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {users.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(perPage) => {
                  setItemsPerPage(perPage);
                  setCurrentPage(1);
                }}
              />
            )}
          </TabsContent>

          {isFullAdmin && (
            <TabsContent value="deleted">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">
                  Users deleted in the last 30 days. After 30 days, users are permanently removed.
                </p>
                <Button variant="outline" onClick={() => setActiveTab('blacklist')}>
                  <Ban className="w-4 h-4 mr-2" />View Blacklist
                </Button>
              </div>

              {/* Bulk Actions Bar */}
              {selectedDeletedUsers.length > 0 && (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900">
                      {selectedDeletedUsers.length} user{selectedDeletedUsers.length > 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedDeletedUsers([])}
                      className="text-gray-600"
                    >
                      <X className="w-4 h-4 mr-1" />Clear
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleBulkRestore}
                      disabled={bulkActionLoading}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      {bulkActionLoading ? 'Restoring...' : 'Restore All'}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setBulkDeleteDialogOpen(true)}
                      disabled={bulkActionLoading}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete Permanently
                    </Button>
                  </div>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={isAllDeletedUsersSelected}
                          onCheckedChange={handleSelectAllDeletedUsers}
                          aria-label="Select all deleted users"
                          className={isSomeDeletedUsersSelected ? "data-[state=checked]:bg-blue-600" : ""}
                        />
                      </TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>INDoS</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Grade/MMD</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Deleted</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingDeleted ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8">Loading...</TableCell></TableRow>
                    ) : deletedUsers.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No deleted users</TableCell></TableRow>
                    ) : (
                      deletedUsers.map(user => (
                        <TableRow key={user.id} className={selectedDeletedUsers.includes(user.id) ? "bg-blue-50" : ""}>
                          <TableCell>
                            <Checkbox
                              checked={selectedDeletedUsers.includes(user.id)}
                              onCheckedChange={(checked) => handleSelectDeletedUser(user.id, checked)}
                              aria-label={`Select ${user.name}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{user.name}</div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-mono text-sm">{user.indos_number || '-'}</div>
                          </TableCell>
                          <TableCell>{getRoleBadge(user.role)}</TableCell>
                          <TableCell>
                            <div className="text-sm">{user.grade || '-'}</div>
                            <div className="text-xs text-gray-500">{user.mmd_location || ''}</div>
                          </TableCell>
                          <TableCell>{getPlanBadge(user.subscription_plan, user.plan_expires_at, user.role, true)}</TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {user.deleted_at ? new Date(user.deleted_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive"><Clock className="w-3 h-3 mr-1" />{user.days_remaining}d left</Badge>
                              <Button size="sm" variant="outline" onClick={() => handleRestore(user.id)} title="Restore">
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handlePermanentDelete(user.id)} title="Delete Permanently">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {deletedUsers.length > 0 && (
                <Pagination
                  currentPage={deletedPage}
                  totalItems={deletedTotal}
                  itemsPerPage={deletedItemsPerPage}
                  onPageChange={setDeletedPage}
                  onItemsPerPageChange={(perPage) => {
                    setDeletedItemsPerPage(perPage);
                    setDeletedPage(1);
                  }}
                />
              )}
            </TabsContent>
          )}

          {/* Blacklist Tab */}
          {isFullAdmin && (
            <TabsContent value="blacklist">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search INDoS or name..."
                      value={blacklistSearch}
                      onChange={(e) => { setBlacklistSearch(e.target.value); setBlacklistPage(1); }}
                      className="pl-9 w-[220px]"
                    />
                  </div>
                  {/* Filter */}
                  <Select value={blacklistFilter} onValueChange={(v) => { setBlacklistFilter(v); setBlacklistPage(1); }}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="permanent">Permanent</SelectItem>
                      <SelectItem value="expiring">Temporary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => setAddBlacklistDialogOpen(true)}>
                  <Ban className="w-4 h-4 mr-2" />Add to Blacklist
                </Button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>INDoS</TableHead>
                      <TableHead>Original User</TableHead>
                      <TableHead>Blocked By</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Blocked On</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingBlacklist ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
                    ) : blacklist.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No blacklisted entries</TableCell></TableRow>
                    ) : (
                      blacklist.map(entry => (
                        <TableRow key={entry.id}>
                          <TableCell>
                            <span className="font-mono font-medium">{entry.indos_number}</span>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{entry.original_user_name || '-'}</div>
                            <div className="text-xs text-gray-500">{entry.original_user_email || ''}</div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{entry.blocked_by_name || 'System'}</div>
                            {entry.blocked_by === 'self' && (
                              <Badge variant="outline" className="text-xs">Self-deleted</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">{entry.reason || '-'}</span>
                          </TableCell>
                          <TableCell>
                            {entry.is_permanent ? (
                              <Badge variant="destructive">Forever</Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 border-amber-300">
                                <Clock className="w-3 h-3 mr-1" />{entry.days_remaining}d left
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {entry.blocked_at ? new Date(entry.blocked_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditBlacklistDialog(entry)}
                                title="Edit duration"
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveFromBlacklist(entry.id)}
                                title="Remove from blacklist"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {blacklist.length > 0 && (
                <Pagination
                  currentPage={blacklistPage}
                  totalItems={blacklistTotal}
                  itemsPerPage={blacklistItemsPerPage}
                  onPageChange={setBlacklistPage}
                  onItemsPerPageChange={(perPage) => {
                    setBlacklistItemsPerPage(perPage);
                    setBlacklistPage(1);
                  }}
                />
              )}
            </TabsContent>
          )}

          {/* Approved Admin Emails Tab */}
          {isFullAdmin && (
            <TabsContent value="approved-emails">
              <div className="flex flex-wrap gap-4 mb-4 items-center justify-between">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search emails..."
                    value={approvedEmailsSearch}
                    onChange={(e) => { setApprovedEmailsSearch(e.target.value); setApprovedEmailsPage(1); }}
                    className="pl-10"
                  />
                </div>
                <Button onClick={() => setAddApprovedEmailDialogOpen(true)} className="bg-[#10B981] hover:bg-[#10B981]/90">
                  <Shield className="w-4 h-4 mr-2" />Add Email
                </Button>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Only users with emails in this list can sign up with @helmtorq.com or @marq.helmtorq.com domains.
              </p>

              {loadingApprovedEmails ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A7EA4]"></div>
                </div>
              ) : approvedEmails.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No approved admin emails yet</p>
                  <p className="text-sm mt-2">Add emails to allow admin domain signups</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {approvedEmails.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-4 bg-white rounded-lg border">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{entry.email}</span>
                          {entry.email.endsWith('@helmtorq.com') ? (
                            <Badge className="bg-purple-100 text-purple-700">Admin</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700">Sub-Admin</Badge>
                          )}
                        </div>
                        {entry.notes && <p className="text-sm text-gray-500 mt-1">{entry.notes}</p>}
                        <p className="text-xs text-gray-400 mt-1">
                          Added by {entry.added_by_name || 'Unknown'} on {new Date(entry.added_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveApprovedEmail(entry.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {approvedEmailsTotal > 20 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={approvedEmailsPage}
                    totalPages={Math.ceil(approvedEmailsTotal / 20)}
                    onPageChange={setApprovedEmailsPage}
                  />
                </div>
              )}
            </TabsContent>
          )}

          {/* Blocked Domains Tab */}
          {isFullAdmin && (
            <TabsContent value="blocked-domains">
              <div className="flex flex-wrap gap-4 mb-4 items-center justify-between">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search domains..."
                    value={blockedDomainsSearch}
                    onChange={(e) => { setBlockedDomainsSearch(e.target.value); setBlockedDomainsPage(1); }}
                    className="pl-10"
                  />
                </div>
                <Button onClick={() => setAddBlockedDomainsDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
                  <Ban className="w-4 h-4 mr-2" />Add Blocked Domains
                </Button>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Users with email addresses from these domains cannot sign up on the platform.
              </p>

              {loadingBlockedDomains ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A7EA4]"></div>
                </div>
              ) : blockedDomains.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Ban className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No blocked domains yet</p>
                  <p className="text-sm mt-2">Add domains to block signups from those addresses</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {blockedDomains.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-red-700">{entry.domain}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Added by {entry.added_by_name || 'Unknown'} on {new Date(entry.added_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveBlockedDomain(entry.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {blockedDomainsTotal > 50 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={blockedDomainsPage}
                    totalPages={Math.ceil(blockedDomainsTotal / 50)}
                    onPageChange={setBlockedDomainsPage}
                  />
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </CardContent>

      {/* Add Blocked Domains Dialog */}
      <Dialog open={addBlockedDomainsDialogOpen} onOpenChange={setAddBlockedDomainsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-600" />Add Blocked Domains
            </DialogTitle>
            <DialogDescription>
              Enter domains to block. Separate multiple domains with commas or new lines.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Domains to block</Label>
              <textarea
                value={newBlockedDomains}
                onChange={(e) => setNewBlockedDomains(e.target.value)}
                placeholder="example.com, spam.org&#10;blocked.net"
                className="w-full mt-2 p-3 border rounded-lg min-h-[120px] text-sm font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Examples: spam.com, temporary-email.org, disposable.net
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBlockedDomainsDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddBlockedDomains} 
              disabled={addingBlockedDomains || !newBlockedDomains.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {addingBlockedDomains ? 'Adding...' : 'Add Domains'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />Delete User Account
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p><strong>Name:</strong> {selectedUser.name}</p>
                <p><strong>INDoS:</strong> {selectedUser.indos_number}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
                <p><strong>Grade:</strong> {selectedUser.grade || 'Not set'}</p>
                <p><strong>Plan:</strong> {selectedUser.subscription_plan === 'paid' ? `Paid (expires: ${selectedUser.plan_expires_at ? new Date(selectedUser.plan_expires_at).toLocaleDateString() : 'N/A'})` : 'Free'}</p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>This will:</strong>
                  <ul className="list-disc list-inside mt-1">
                    <li>Remove user&apos;s access immediately</li>
                    <li>Keep in deleted users for 30 days</li>
                    <li>Block new signups with this INDoS</li>
                  </ul>
                </p>
              </div>

              <div>
                <Label>Block signup duration</Label>
                <Select value={blacklistDuration} onValueChange={setBlacklistDuration}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                    <SelectItem value="forever">Forever</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Type INDoS to confirm: <strong>{selectedUser.indos_number}</strong></Label>
                <Input
                  value={deleteConfirmIndos}
                  onChange={(e) => setDeleteConfirmIndos(e.target.value)}
                  placeholder="Enter INDoS number"
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deletingUser || deleteConfirmIndos.toUpperCase() !== selectedUser?.indos_number?.toUpperCase()}
            >
              {deletingUser ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Change Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Change Subscription Plan</DialogTitle>
            {selectedUser && (
              <DialogDescription>
                {selectedUser.name} • <span className="font-mono">{selectedUser.indos_number}</span>
              </DialogDescription>
            )}
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 overflow-y-auto max-h-[60vh] pr-2">
              {/* Mode selector — only offered to paid users. Free users go
                  straight through the upgrade flow as before. */}
              {selectedUser.subscription_plan === 'paid' && !['admin', 'sub_admin'].includes(selectedUser.role) && (
                <div className="p-4 border border-gray-200 rounded-lg" data-testid="plan-mode-selector">
                  <Label className="text-sm font-medium text-gray-800 mb-2 block">What do you want to do?</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPlanChangeMode('switch')}
                      className={`text-left border rounded-md p-3 text-xs transition ${planChangeMode === 'switch' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                      data-testid="plan-mode-switch"
                    >
                      <div className="font-medium">Change plan / grade / expiry</div>
                      <div className="text-gray-600 mt-0.5">Keeps user on a paid plan. Non-destructive.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPlanChangeMode('to_free')}
                      className={`text-left border rounded-md p-3 text-xs transition ${planChangeMode === 'to_free' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'}`}
                      data-testid="plan-mode-to-free"
                    >
                      <div className="font-medium">Move to Free</div>
                      <div className="text-gray-600 mt-0.5">Immediately revokes paid access.</div>
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Current:</p>
                  {getPlanBadge(selectedUser.subscription_plan, selectedUser.plan_expires_at, selectedUser.role)}
                </div>
                <div className="text-2xl text-gray-400">→</div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">New:</p>
                  {(() => {
                    // Compute the visual "new" badge based on mode.
                    const isPaid = selectedUser.subscription_plan === 'paid';
                    if (isPaid && planChangeMode === 'to_free') {
                      return getPlanBadge('free', null, selectedUser.role, false, true);
                    }
                    if (isPaid && planChangeMode === 'switch') {
                      // Show projected expiry — depends on expiryMode.
                      let projectedExpiry = selectedUser.plan_expires_at;
                      if (expiryMode === 'reset') {
                        projectedExpiry = new Date(Date.now() + selectedExpiryDays * 24 * 60 * 60 * 1000).toISOString();
                      } else if (expiryMode === 'prorate') {
                        projectedExpiry = null;   // server computes; show ambient badge
                      }
                      return getPlanBadge('paid', projectedExpiry, selectedUser.role, false, true);
                    }
                    // Free user upgrading.
                    return getPlanBadge('paid', null, selectedUser.role, false, true);
                  })()}
                </div>
              </div>

              {/* Paid-plan fields — shown for free-upgrade AND for paid switch */}
              {(selectedUser.subscription_plan !== 'paid' || planChangeMode === 'switch') && !['admin', 'sub_admin'].includes(selectedUser.role) && (
                <>
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <Label className="text-sm font-medium text-purple-800">Premium Access Grade</Label>
                    <Select value={selectedSubscribedGrade} onValueChange={handleGradeChangeInUpgrade}>
                      <SelectTrigger className="mt-2 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {availableGrades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Plan Selection - Required */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <Label className="text-sm font-medium text-green-800">
                      Select Plan <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs text-green-600 mb-2">Module access will be imported from the selected plan</p>
                    {loadingPlans ? (
                      <div className="py-2 text-sm text-gray-500">Loading plans...</div>
                    ) : availablePlans.length === 0 ? (
                      <div className="py-2 text-sm text-red-500">No plans available for {selectedSubscribedGrade}. Please create a plan first.</div>
                    ) : (
                      <Select value={selectedPlanId} onValueChange={handlePlanSelectionChange}>
                        <SelectTrigger className={`mt-2 bg-white ${!selectedPlanId ? 'border-red-300' : ''}`}>
                          <SelectValue placeholder="Select a plan..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {/* Grade-specific plans */}
                          {getGroupedPlans().gradeSpecific.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50">
                                {selectedSubscribedGrade} Plans
                              </div>
                              {getGroupedPlans().gradeSpecific.map(plan => (
                                <SelectItem key={plan.id} value={plan.id}>
                                  {plan.name} - ₹{plan.price}
                                </SelectItem>
                              ))}
                            </>
                          )}
                          {/* All Grades plans */}
                          {getGroupedPlans().allGrades.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 mt-1">
                                All Grades
                              </div>
                              {getGroupedPlans().allGrades.map(plan => (
                                <SelectItem key={plan.id} value={plan.id}>
                                  {plan.name} - ₹{plan.price}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                    {selectedPlanModuleAccess && (
                      <div className="mt-3 p-2 bg-white rounded border">
                        <p className="text-xs font-medium text-gray-600 mb-1">Module Access from Plan:</p>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(selectedPlanModuleAccess).map(([key, value]) => (
                            <Badge 
                              key={key} 
                              variant="outline" 
                              className={`text-xs ${value ? 'bg-green-50 text-green-700 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}
                            >
                              {value ? '✓' : '✗'} {key.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <Label className="text-sm font-medium text-blue-800">Plan Duration</Label>
                    {selectedUser.subscription_plan === 'paid' && planChangeMode === 'switch' ? (
                      <>
                        <p className="text-xs text-blue-600 mb-3">Choose how the current subscription rolls into the new plan.</p>
                        <div className="space-y-2">
                          <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded hover:bg-blue-100">
                            <input type="radio" name="expiryMode" value="keep" checked={expiryMode === 'keep'} onChange={() => setExpiryMode('keep')} data-testid="expiry-mode-keep" className="mt-0.5" />
                            <div>
                              <div className="font-medium text-blue-900">Keep current expiry</div>
                              <div className="text-blue-700">
                                {selectedUser.plan_expires_at ? new Date(selectedUser.plan_expires_at).toLocaleDateString() : 'No expiry set'} — non-destructive default
                              </div>
                            </div>
                          </label>
                          <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded hover:bg-blue-100">
                            <input type="radio" name="expiryMode" value="prorate" checked={expiryMode === 'prorate'} onChange={() => setExpiryMode('prorate')} data-testid="expiry-mode-prorate" className="mt-0.5" />
                            <div>
                              <div className="font-medium text-blue-900">Prorate — carry unused value into new plan</div>
                              <div className="text-blue-700">Fair to the user in both directions. Server computes the new expiry.</div>
                            </div>
                          </label>
                          <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded hover:bg-blue-100">
                            <input type="radio" name="expiryMode" value="reset" checked={expiryMode === 'reset'} onChange={() => setExpiryMode('reset')} data-testid="expiry-mode-reset" className="mt-0.5" />
                            <div className="flex-1">
                              <div className="font-medium text-blue-900">Reset expiry</div>
                              <div className="text-blue-700 mb-1">Fresh clock starts today.</div>
                              {expiryMode === 'reset' && (
                                <Select value={selectedExpiryDays.toString()} onValueChange={(v) => setSelectedExpiryDays(parseInt(v))}>
                                  <SelectTrigger className="mt-1 bg-white h-8"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="7">7 days</SelectItem>
                                    <SelectItem value="30">30 days</SelectItem>
                                    <SelectItem value="90">90 days</SelectItem>
                                    <SelectItem value="180">180 days</SelectItem>
                                    <SelectItem value="365">1 year</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </label>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-blue-600 mb-2">Admin decides the validity period</p>
                        <Select value={selectedExpiryDays.toString()} onValueChange={(v) => setSelectedExpiryDays(parseInt(v))}>
                          <SelectTrigger className="mt-2 bg-white"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">7 days</SelectItem>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                            <SelectItem value="180">180 days</SelectItem>
                            <SelectItem value="365">1 year</SelectItem>
                          </SelectContent>
                        </Select>
                      </>
                    )}
                  </div>
                  {/* Module Access Override */}
                  <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <Checkbox
                      id="module_access_override"
                      checked={moduleAccessOverride}
                      onCheckedChange={(checked) => setModuleAccessOverride(checked)}
                    />
                    <div>
                      <Label htmlFor="module_access_override" className="text-amber-800 font-medium cursor-pointer">
                        Grant All Modules (Override)
                      </Label>
                      <p className="text-xs text-amber-700">
                        Bypass plan restrictions and grant access to all premium modules
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePlanUpdate} disabled={updatingPlan}>
              {updatingPlan ? 'Updating...' : 'Update Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialogOpen} onOpenChange={setViewDetailsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {loadingDetails ? (
            <p className="text-center py-4">Loading...</p>
          ) : userDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-gray-500">Name</Label><p className="font-medium">{userDetails.name}</p></div>
                <div><Label className="text-xs text-gray-500">Email</Label><p>{userDetails.email}</p></div>
                <div><Label className="text-xs text-gray-500">INDoS</Label><p className="font-mono">{userDetails.indos_number || '-'}</p></div>
                <div><Label className="text-xs text-gray-500">Mobile</Label><p>{userDetails.mobile_number || '-'}</p></div>
                <div><Label className="text-xs text-gray-500">DOB</Label><p>{userDetails.date_of_birth || '-'}</p></div>
                <div><Label className="text-xs text-gray-500">Role</Label><p>{getRoleBadge(userDetails.role)}</p></div>
                <div><Label className="text-xs text-gray-500">Grade</Label><p>{userDetails.grade || '-'}</p></div>
                <div><Label className="text-xs text-gray-500">MMD</Label><p>{userDetails.mmd_location || '-'}</p></div>
                <div><Label className="text-xs text-gray-500">Plan</Label><p>{getPlanBadge(userDetails.subscription_plan, userDetails.plan_expires_at, userDetails.role)}</p></div>
                <div><Label className="text-xs text-gray-500">Joined</Label><p>{userDetails.created_at ? new Date(userDetails.created_at).toLocaleDateString() : '-'}</p></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={editFormData.name} onChange={(e) => setEditFormData(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label>INDoS Number</Label>
              <Input value={editFormData.indos_number} onChange={(e) => setEditFormData(f => ({ ...f, indos_number: e.target.value }))} />
            </div>
            <div>
              <Label>Date of Birth</Label>
              <DOBDatePicker 
                date={editFormData.date_of_birth ? new Date(editFormData.date_of_birth) : null} 
                onDateChange={(date) => setEditFormData(f => ({ ...f, date_of_birth: date ? format(date, 'yyyy-MM-dd') : '' }))}
                placeholder="Select date of birth"
              />
            </div>
            <div>
              <Label>Mobile Number</Label>
              <Input value={editFormData.mobile_number} onChange={(e) => setEditFormData(f => ({ ...f, mobile_number: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Generate a new password for {selectedUser?.name}</DialogDescription>
          </DialogHeader>
          {(newPassword || emailSentInfo) ? (
            <div className="space-y-4">
              {newPassword ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 mb-2">New temporary password:</p>
                  <div className="flex items-center gap-2">
                    <code data-testid="reset-password-display" className="flex-1 p-2 bg-white rounded border font-mono">{newPassword}</code>
                    <Button size="sm" variant="outline" onClick={copyPassword} data-testid="copy-password-btn">
                      {passwordCopied ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ) : null}

              {emailSentInfo ? (
                emailSentInfo.sent ? (
                  <div data-testid="email-sent-badge" className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                    <span>A combined security alert + new-password email was sent to <strong>{emailSentInfo.to}</strong>. The user will be forced to change this password on their next login.</span>
                  </div>
                ) : (
                  <div data-testid="email-failed-badge" className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
                    <p className="font-medium mb-1">Email delivery failed</p>
                    <p className="text-xs">Please share the password with <strong>{emailSentInfo.to}</strong> through a secure channel.</p>
                    {emailSentInfo.error ? <p className="text-xs mt-1 text-amber-700">Reason: {emailSentInfo.error}</p> : null}
                  </div>
                )
              ) : null}

              <p className="text-xs text-gray-500">
                The user&rsquo;s active sessions have been invalidated and they must choose a new password before continuing.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600">This will generate a new random password for the user and email it to them. The user will be forced to choose a new password on next login.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>Close</Button>
            {!newPassword && !emailSentInfo && (
              <Button onClick={handleResetPassword} disabled={resettingPassword} data-testid="confirm-reset-password-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${resettingPassword ? 'animate-spin' : ''}`} />
                {resettingPassword ? 'Generating...' : 'Generate Password'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>Update role for {selectedUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger data-testid="role-select-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="sub_admin">Sub Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            {selectedRole === 'sub_admin' && (
              <div className="space-y-4" data-testid="sub-admin-permissions-grid">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Sub-Admin Capabilities:</strong> Grant only the areas this user should manage. Unchecked items stay hidden or read-only. <em>Manage Classes</em> automatically includes <em>Host Classes</em>.
                  </p>
                </div>
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                    {selectedPermissions.length} of {ALL_SUB_ADMIN_CAPS.length} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedPermissions([...ALL_SUB_ADMIN_CAPS])}
                      className="text-xs text-[#0A7EA4] hover:underline"
                      data-testid="select-all-caps-btn"
                    >
                      Select all
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      type="button"
                      onClick={() => setSelectedPermissions([])}
                      className="text-xs text-slate-500 hover:underline"
                      data-testid="clear-all-caps-btn"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="space-y-4">
                  {CAP_GROUPS.map((group) => (
                    <div key={group.module} className="space-y-2" data-testid={`cap-group-${group.module.replace(/\s+/g, '-').toLowerCase()}`}>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">{group.module}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {group.caps.map(({ key, title, desc }) => {
                          const implied = isImpliedBySelected(key);
                          const checked = selectedPermissions.includes(key) || implied;
                          return (
                            <label
                              key={key}
                              htmlFor={`cap-${key}`}
                              className={`flex items-start gap-2 p-3 rounded-lg border transition-colors min-w-0 ${
                                implied ? 'border-[#0A7EA4]/50 bg-[#0A7EA4]/5 opacity-80 cursor-not-allowed' :
                                checked ? 'border-[#0A7EA4] bg-[#0A7EA4]/5 cursor-pointer' :
                                'border-slate-200 hover:border-slate-300 bg-white cursor-pointer'
                              }`}
                              data-testid={`cap-row-${key}`}
                            >
                              <Checkbox
                                id={`cap-${key}`}
                                checked={checked}
                                disabled={implied}
                                onCheckedChange={() => !implied && togglePermission(key)}
                                className="mt-0.5"
                                data-testid={`cap-checkbox-${key}`}
                              />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-slate-900 break-words">
                                  {title}
                                  {implied && <span className="ml-1 text-[10px] font-normal text-[#0A7EA4]">(auto)</span>}
                                </p>
                                <p className="text-xs text-slate-500 break-words">{desc}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {selectedPermissions.length === 0 && (
                  <p className="text-xs text-amber-600" data-testid="no-caps-warning">
                    No capabilities selected. This sub-admin will only be able to view the admin dashboard shell.
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRoleUpdate} data-testid="confirm-role-update-btn">Update Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset 2FA Dialog */}
      <Dialog open={reset2FADialogOpen} onOpenChange={setReset2FADialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#0A7EA4]" />Reset Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              This will disable 2FA for <strong>{selectedUser?.name}</strong>. They will be required to set up 2FA again on their next login.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> The user&apos;s existing backup codes will be invalidated. Make sure the user is aware they need to set up 2FA again.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReset2FADialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReset2FAConfirm} disabled={resetting2FA}>
              {resetting2FA ? 'Resetting...' : 'Reset 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />Permanently Delete {selectedDeletedUsers.length} User{selectedDeletedUsers.length > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The selected users will be permanently removed from the system.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> You are about to permanently delete {selectedDeletedUsers.length} user{selectedDeletedUsers.length > 1 ? 's' : ''}.
                <ul className="list-disc list-inside mt-2">
                  <li>All user data will be permanently removed</li>
                  <li>Blacklist entries will remain active</li>
                  <li>This action cannot be reversed</li>
                </ul>
              </p>
            </div>
            
            <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Selected users:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                {deletedUsers
                  .filter(u => selectedDeletedUsers.includes(u.id))
                  .map(u => (
                    <li key={u.id} className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-gray-200 px-1 rounded">{u.indos_number}</span>
                      <span>{u.name}</span>
                    </li>
                  ))
                }
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkActionLoading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkPermanentDelete}
              disabled={bulkActionLoading}
            >
              {bulkActionLoading ? 'Deleting...' : `Delete ${selectedDeletedUsers.length} User${selectedDeletedUsers.length > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Blacklist Dialog */}
      <Dialog open={addBlacklistDialogOpen} onOpenChange={setAddBlacklistDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-600" />Add to Blacklist
            </DialogTitle>
            <DialogDescription>
              Manually add an INDoS number to the blacklist to prevent registration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="blacklist-indos">INDoS Number *</Label>
              <Input
                id="blacklist-indos"
                value={newBlacklistIndos}
                onChange={(e) => setNewBlacklistIndos(e.target.value.toUpperCase())}
                placeholder="Enter INDoS number"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="blacklist-duration">Block Duration</Label>
              <Select value={newBlacklistDuration} onValueChange={setNewBlacklistDuration}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                  <SelectItem value="forever">Forever</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="blacklist-reason">Reason (optional)</Label>
              <Input
                id="blacklist-reason"
                value={newBlacklistReason}
                onChange={(e) => setNewBlacklistReason(e.target.value)}
                placeholder="e.g., Fraudulent activity"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBlacklistDialogOpen(false)} disabled={addingToBlacklist}>
              Cancel
            </Button>
            <Button onClick={handleAddToBlacklist} disabled={addingToBlacklist || !newBlacklistIndos.trim()}>
              {addingToBlacklist ? 'Adding...' : 'Add to Blacklist'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Blacklist Duration Dialog */}
      <Dialog open={editBlacklistDialogOpen} onOpenChange={setEditBlacklistDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />Edit Block Duration
            </DialogTitle>
            <DialogDescription>
              Change the block duration for this INDoS number.
            </DialogDescription>
          </DialogHeader>
          {editingBlacklistEntry && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm"><strong>INDoS:</strong> {editingBlacklistEntry.indos_number}</p>
                {editingBlacklistEntry.original_user_name && (
                  <p className="text-sm"><strong>User:</strong> {editingBlacklistEntry.original_user_name}</p>
                )}
                <p className="text-sm">
                  <strong>Current:</strong>{' '}
                  {editingBlacklistEntry.is_permanent ? 'Forever' : `${editingBlacklistEntry.days_remaining} days remaining`}
                </p>
              </div>
              <div>
                <Label>New Duration</Label>
                <Select value={editBlacklistDuration} onValueChange={setEditBlacklistDuration}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days (from now)</SelectItem>
                    <SelectItem value="90">90 days (from now)</SelectItem>
                    <SelectItem value="180">180 days (from now)</SelectItem>
                    <SelectItem value="365">1 year (from now)</SelectItem>
                    <SelectItem value="forever">Forever</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Duration will be calculated from today.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBlacklistDialogOpen(false)} disabled={updatingBlacklistDuration}>
              Cancel
            </Button>
            <Button onClick={handleEditBlacklistDuration} disabled={updatingBlacklistDuration}>
              {updatingBlacklistDuration ? 'Updating...' : 'Update Duration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Approved Email Dialog */}
      <Dialog open={addApprovedEmailDialogOpen} onOpenChange={setAddApprovedEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#10B981]" />Add Approved Admin Email
            </DialogTitle>
            <DialogDescription>
              Add an email to allow signup with admin domains (@helmtorq.com or @marq.helmtorq.com)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="name@helmtorq.com"
                value={newApprovedEmail}
                onChange={(e) => setNewApprovedEmail(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                @helmtorq.com → Admin role, @marq.helmtorq.com → Sub-Admin role
              </p>
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="e.g., Marketing Team Lead"
                value={newApprovedEmailNotes}
                onChange={(e) => setNewApprovedEmailNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddApprovedEmailDialogOpen(false)} disabled={addingApprovedEmail}>
              Cancel
            </Button>
            <Button onClick={handleAddApprovedEmail} disabled={addingApprovedEmail} className="bg-[#10B981] hover:bg-[#10B981]/90">
              {addingApprovedEmail ? 'Adding...' : 'Add Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upgrade Dialog */}
      <Dialog open={bulkUpgradeDialogOpen} onOpenChange={setBulkUpgradeDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Bulk Upgrade Users</DialogTitle>
            <DialogDescription>
              Upgrade {selectedUserIds.size} selected user{selectedUserIds.size > 1 ? 's' : ''} to a paid plan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 overflow-y-auto max-h-[60vh] pr-2">
            {/* Selected Users Preview */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-sm font-medium text-blue-800 mb-2 block">Selected Users:</Label>
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                {getSelectedUsers().slice(0, 10).map(user => (
                  <Badge key={user.id} variant="secondary" className="text-xs">
                    {user.name}
                  </Badge>
                ))}
                {selectedUserIds.size > 10 && (
                  <Badge variant="secondary" className="text-xs bg-gray-200">
                    +{selectedUserIds.size - 10} more
                  </Badge>
                )}
              </div>
            </div>

            {/* Grade Selection */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <Label className="text-sm font-medium text-purple-800">Premium Access Grade</Label>
              <p className="text-xs text-purple-600 mb-2">All users will be subscribed to this grade</p>
              <Select value={selectedSubscribedGrade} onValueChange={handleGradeChangeInUpgrade}>
                <SelectTrigger className="mt-2 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableGrades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            {/* Plan Selection - Required */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <Label className="text-sm font-medium text-green-800">
                Select Plan <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-green-600 mb-2">All users will be assigned to this plan</p>
              {loadingPlans ? (
                <div className="py-2 text-sm text-gray-500">Loading plans...</div>
              ) : availablePlans.length === 0 ? (
                <div className="py-2 text-sm text-red-500">No plans available for {selectedSubscribedGrade}</div>
              ) : (
                <Select value={selectedPlanId} onValueChange={handlePlanSelectionChange}>
                  <SelectTrigger className={`mt-2 bg-white ${!selectedPlanId ? 'border-red-300' : ''}`}>
                    <SelectValue placeholder="Select a plan..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {/* Grade-specific plans */}
                    {getGroupedPlans().gradeSpecific.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50">
                          {selectedSubscribedGrade} Plans
                        </div>
                        {getGroupedPlans().gradeSpecific.map(plan => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name} - ₹{plan.price}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {/* All Grades plans */}
                    {getGroupedPlans().allGrades.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 mt-1">
                          All Grades
                        </div>
                        {getGroupedPlans().allGrades.map(plan => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name} - ₹{plan.price}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              )}
              {selectedPlanModuleAccess && (
                <div className="mt-3 p-2 bg-white rounded border">
                  <p className="text-xs font-medium text-gray-600 mb-1">Module Access from Plan:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(selectedPlanModuleAccess).map(([key, value]) => (
                      <Badge 
                        key={key} 
                        variant="outline" 
                        className={`text-xs ${value ? 'bg-green-50 text-green-700 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}
                      >
                        {value ? '✓' : '✗'} {key.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Duration Selection */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-sm font-medium text-blue-800">Plan Duration</Label>
              <p className="text-xs text-blue-600 mb-2">Same duration applied to all users</p>
              <Select value={selectedExpiryDays.toString()} onValueChange={(v) => setSelectedExpiryDays(parseInt(v))}>
                <SelectTrigger className="mt-2 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Module Access Override */}
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Checkbox
                id="bulk_module_access_override"
                checked={moduleAccessOverride}
                onCheckedChange={(checked) => setModuleAccessOverride(checked)}
              />
              <div>
                <Label htmlFor="bulk_module_access_override" className="text-amber-800 font-medium cursor-pointer">
                  Grant All Modules (Override)
                </Label>
                <p className="text-xs text-amber-700">
                  Bypass plan restrictions for all selected users
                </p>
              </div>
            </div>

            {/* Progress indicator during upgrade */}
            {bulkUpgrading && (
              <div className="p-4 bg-gray-50 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Upgrading users...</span>
                  <span className="text-sm text-gray-600">
                    {bulkUpgradeProgress.current} / {bulkUpgradeProgress.total}
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all duration-300"
                    style={{ width: `${(bulkUpgradeProgress.current / bulkUpgradeProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setBulkUpgradeDialogOpen(false)} disabled={bulkUpgrading}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpgrade} 
              disabled={bulkUpgrading || !selectedPlanId}
              className="bg-green-600 hover:bg-green-700"
            >
              {bulkUpgrading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Upgrading...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Upgrade {selectedUserIds.size} User{selectedUserIds.size > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <ExportModal
        open={showExportModal}
        onOpenChange={setShowExportModal}
        title="Export Users"
        description="Export user data based on current filters. Only filtered data will be exported."
        filters={[]}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'indos_number', label: 'INDoS Number' },
          { key: 'mobile_number', label: 'Mobile' },
          { key: 'role', label: 'Role' },
          { key: 'subscription_status', label: 'Subscription Status' },
          { key: 'subscription_plan', label: 'Plan' },
          { key: 'subscribed_grade', label: 'Subscribed Grade' },
          { key: 'plan_expires_at', label: 'Plan Expires' },
          { key: 'coins_balance', label: 'Coins Balance' },
          { key: 'coins_earned_total', label: 'Total Earned' },
          { key: 'coins_redeemed_total', label: 'Total Redeemed' },
          { key: 'is_active', label: 'Active' },
          { key: 'created_at', label: 'Joined At' },
          { key: 'last_login', label: 'Last Login' }
        ]}
        onExport={handleExport}
        exporting={exporting}
        totalCount={exportCount}
      />
      <UserActivityDrawer
        open={!!activityDrawerUser}
        onClose={() => setActivityDrawerUser(null)}
        targetUserId={activityDrawerUser?.id}
        targetUserName={activityDrawerUser?.name}
        allowJumpToFullLog={isFullAdmin}
        onJumpToFullLog={() => { window.location.href = '/admin/tools/user-activity'; }}
      />
    </Card>
  );
};

export default UserManagement;
